import type { Session } from '@kodall/kodall-client/dist/types'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useAuthStore = defineStore('auth', () => {
  const status = ref<'authenticated' | 'unauthenticated'>('unauthenticated')
  const user = ref<Session | null>(null)

  /** Mark user as authenticated and persist to localStorage */
  function setAuthenticated(userData: Session) {
    status.value = 'authenticated'
    user.value = userData

    if (process.client) {
      try {
        localStorage.setItem('auth', JSON.stringify({ status: status.value, user: user.value }))
      } catch (err) {
        console.warn('Failed to save auth state', err)
      }
    }
  }

  /** Mark user as unauthenticated and clear storage */
  function setUnauthenticated() {
    status.value = 'unauthenticated'
    user.value = null

    if (process.client) {
      try {
        localStorage.removeItem('auth')
      } catch (err) {
        console.warn('Failed to remove auth state', err)
      }
    }
  }

  const isLoaded = ref(false)

  /** Load persisted auth state from localStorage */
  function loadFromStorage() {
    if (!process.client || isLoaded.value) return
    try {
      const stored = localStorage.getItem('auth')
      if (stored) {
        const parsed = JSON.parse(stored)
        status.value = parsed.status
        user.value = parsed.user
      }
    } catch (err) {
      console.warn('Failed to load auth state', err)
    } finally {
      isLoaded.value = true
    }
  }

  return { status, user, setAuthenticated, setUnauthenticated, loadFromStorage }
})
